import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNodesState, useEdgesState } from '@xyflow/react'
import { Play, Pause, Save, RotateCcw, Home, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Square, AlertCircle, RefreshCw, Pencil, Check, X, Loader2, Download, Upload, Wand2 } from 'lucide-react'
import NodePalette from './NodePalette'
import WorkflowCanvas from './WorkflowCanvas'
import NodeConfigPanel from './NodeConfigPanel'
import ExecutionLogPanel from './ExecutionLogPanel'
import SavedFlowsPanel from '../workflow/SavedFlowsPanel'
import SaveFlowDialog from '../workflow/SaveFlowDialog'
import Modal, { Button, FormActions } from '../shared/Modal'
import AnalyzeView from '../views/AnalyzeView'
import MapView from '../views/MapView'
import DecomposeView from '../views/DecomposeView'
import DataView from '../views/DataView'
import DiscoveryView from '../views/DiscoveryView'
import ArchitectView from '../views/ArchitectView'
import CodeEditorView from '../views/CodeEditorView'
import RefactorReportView from '../views/RefactorReportView'
import AiNamingReportView from '../views/AiNamingReportView'
import JavaValidationAnalysisView from '../views/JavaValidationAnalysisView'
import GateReportView from '../views/GateReportView'
import ResolveIssuesView from '../views/ResolveIssuesView'
import ServiceGenerationView from '../views/ServiceGenerationView'
import AIAssistant from './AIAssistant'
import Tooltip from '../shared/Tooltip'
import AssistanceWizard from './AssistanceWizard'
import { UI_TOOLTIPS } from '../../constants/tooltipContent'
import { useWorkflow } from '../../hooks'
import { getAPIBaseUrl } from '../../environment'
import { generateTemplateNodes } from '../../data/nodeRegistry'
import './DesignerView.css'

// Get API base URL from environment (reads from .env VITE_API_URL or window.__MODERNIZEIT_API_URL__)
const DEFAULT_API_BASE_URL = getAPIBaseUrl()

// Maps pipeline-* nodeType (stored in WorkflowRuns.job_ids) → internal jobs object key
const NODE_TYPE_TO_JOB_KEY = {
  'pipeline-upload':          'ingest_job_id',
  'pipeline-code-analysis':   'codeanalysis_job_id',
  'pipeline-refactor':        'coderefactor_job_id',
  'pipeline-ai-naming':       'ai_naming_job_id',
  'pipeline-dependencies':    'dependencymapper_job_id',
  'pipeline-monolith':        'monolithidentifier_job_id',
  'pipeline-data':            'dataanalysis_job_id',
  'pipeline-discovery':       'discovery_job_id',
  'pipeline-architecture':    'architecture_job_id',
  'pipeline-service-gen':     'service_generation_job_id',
  'pipeline-pre-flight':      'pre_flight_job_id',
  'pipeline-java-package':    'java_packaging_job_id',
  'pipeline-java-validation': 'java_validation_job_id',
}

// Map of view names to components
const PHASE_VIEWS = {
  analyze: AnalyzeView,
  map: MapView,
  decompose: DecomposeView,
  data: DataView,
  discovery: DiscoveryView,
  architect: ArchitectView,
  'code-editor': CodeEditorView,
  'service-gen-editor': ServiceGenerationView,
  'refactor-report': RefactorReportView,
  'ai-naming-report': AiNamingReportView,
  'java-validation-report': JavaValidationAnalysisView,
  'gate-report': GateReportView,
  'resolve-issues': ResolveIssuesView,
}

function DesignerView({ project, run, onBackToLanding, onRegisterActions }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedNodeId, setSelectedNodeId] = useState(null)

  // Project comes from App.jsx (selected from LandingView)
  // Contains: { id, account, accountName, name }

  // Get workflow management from hook (use project.id for application)
  const {
    currentWorkflow,
    saving,
    executing,
    paused,
    pausedAtNode,
    executionProgress,
    executionLogs,
    saveWorkflow,
    loadWorkflow,
    executeWorkflow,
    abortExecution,
    pauseExecution,
    resumeExecution,
    addLog,
    clearLogs,
    updateWorkflowNodes,
    updateWorkflowEdges,
  } = useWorkflow(project?.id)

  // Panel collapse state
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)

  // Assistance wizard — opened from the Assistance button in the toolbar center
  const [showAssistanceWizard, setShowAssistanceWizard] = useState(false)

  // Phase view state (null = show workflow canvas, 'analyze'/'map'/'decompose' = show that view)
  const [activePhaseView, setActivePhaseView] = useState(null)
  // Tracks the view that was active before resolve-issues was opened, for correct onBack routing
  const previousPhaseViewRef = useRef(null)
  // Store context when opening a view (so it's not lost when state changes)
  const [viewContext, setViewContext] = useState(null)

  // Error modal state
  const [errorModal, setErrorModal] = useState({ open: false, nodeLabel: '', error: '' })

  // Workflow name editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const nameInputRef = useRef(null)

  // Save Flow Dialog state
  const [saveFlowDialogOpen, setSaveFlowDialogOpen] = useState(false)
  const [flowsRefreshTrigger, setFlowsRefreshTrigger] = useState(0)

  // Track which run we've already restored so we don't re-apply on every render
  const runRestoredRef = useRef(null)

  // Reset the restoration flag whenever the run prop changes (different card clicked)
  useEffect(() => {
    runRestoredRef.current = null
  }, [run?.workflow_run_id])

  // Restore node completion states when loading a historical run (or reset for fresh runs)
  useEffect(() => {
    if (!run) return
    if (!nodes.length) return

    // Use a stable key to track which run state we've applied
    const runKey = run.workflow_run_id || (run._isNew ? '__new__' : null)
    if (!runKey) return
    if (runRestoredRef.current === runKey) return
    runRestoredRef.current = runKey

    // Fresh run: reset all node statuses so the canvas is clean
    if (run._isNew || !run.job_ids) {
      setNodes(nds => nds.map(node => ({
        ...node,
        data: { ...node.data, status: null, executionResult: null, error: null },
      })))
      return
    }

    const jobIdsByNodeType = run.job_ids  // { 'pipeline-code-analysis': 'abc123', ... }

    // Build the internal jobs object { codeanalysis_job_id: 'abc123', ... }
    const allJobs = {}
    for (const [nodeType, jobId] of Object.entries(jobIdsByNodeType)) {
      const key = NODE_TYPE_TO_JOB_KEY[nodeType]
      if (key && jobId) allJobs[key] = jobId
    }

    const token = localStorage.getItem('modernizeit_token')
    const caJobId     = allJobs.codeanalysis_job_id
    const ingestJobId = allJobs.ingest_job_id

    const fetchJob = (jobId) => jobId
      ? fetch(`${DEFAULT_API_BASE_URL}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => data?.output_data || null)
          .catch(() => null)
      : Promise.resolve(null)

    // Fetch CA output (main_program, base_name) + ingest output (source_hash) in parallel.
    // source_hash is required by ingestExecutor to skip re-upload on re-run.
    Promise.all([fetchJob(caJobId), fetchJob(ingestJobId)]).then(([caOutput, ingestOutput]) => {
      const mainProgram = caOutput?.main_program || null
      const baseName = caOutput?.base_name ||
        (mainProgram ? mainProgram.toLowerCase().replace(/\.cbl$/i, '').replace(/\.cob$/i, '') : null)
      const sourceHash = ingestOutput?.source_hash || null

      console.log(' [RUN_RESTORE] Injecting run state:', {
        run_id: run.workflow_run_id,
        jobCount: Object.keys(allJobs).length,
        mainProgram, baseName, sourceHash,
      })

      setNodes(nds => nds.map(node => {
        const nodeType = node.data?.nodeType
        const hasJob = !!jobIdsByNodeType[nodeType]
        if (!hasJob) return node

        return {
          ...node,
          data: {
            ...node.data,
            status: 'complete',
            executionResult: {
              // Spread CA output fields (main_program, artifacts_path, etc.)
              ...(caOutput || {}),
              // source_hash from ingest — lets ingestExecutor skip re-upload on re-run
              ...(ingestOutput || {}),
              jobs: allJobs,
              main_program: mainProgram,
              base_name: baseName,
              source_hash: sourceHash,
            },
          },
        }
      }))
    })
  }, [run, nodes.length, setNodes])

  // Sync local state with workflow from hook
  useEffect(() => {
    if (currentWorkflow?.nodes) {
      setNodes(currentWorkflow.nodes)
    }
    if (currentWorkflow?.edges) {
      setEdges(currentWorkflow.edges)
    }
  }, [currentWorkflow, setNodes, setEdges])

  // Sync execution progress with node status and results
  useEffect(() => {
    if (!executionProgress || Object.keys(executionProgress).length === 0) return

    setNodes((nds) =>
      nds.map((node) => {
        const progress = executionProgress[node.id]
        if (!progress) return node

        // Map execution status to node status
        let status = null
        let error = null
        let executionResult = node.data?.executionResult // Preserve existing result

        switch (progress.status) {
          case 'running':
            status = 'running'
            break
          case 'completed':
            status = 'complete'
            // Store the execution result on the node
            executionResult = progress.result
            console.log(`[DesignerView] Node ${node.id} completed, storing result:`, {
              hasResult: !!progress.result,
              resultKeys: progress.result ? Object.keys(progress.result) : [],
              documentation: !!progress.result?.documentation,
              output: !!progress.result?.output
            })
            break
          case 'error':
            status = 'error'
            error = progress.message
            // Persist partial result (nodeResult attached by executors on failure) so
            // the Results panel and Download Artifacts button work for failed nodes.
            if (progress.result) executionResult = progress.result
            break
          case 'paused':
            status = 'paused'
            // Paused is a safe boundary - preserve result if node completed before pause
            if (progress.result) {
              executionResult = progress.result
              console.log(`[DesignerView] Node ${node.id} paused with result:`, {
                hasResult: !!progress.result,
                resultKeys: progress.result ? Object.keys(progress.result) : []
              })
            }
            break
          case 'skipped':
            status = null // Reset to default
            break
          default:
            break
        }

        return {
          ...node,
          data: {
            ...node.data,
            status,
            error,
            executionResult,
          },
        }
      })
    )
  }, [executionProgress, setNodes])

  // Focus name input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  // Get selected node object
  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null

  // Handle node selection
  const handleNodeSelect = useCallback((nodeId) => {
    setSelectedNodeId(nodeId)

    // Check if the clicked node has an error - show error modal
    if (nodeId) {
      const node = nodes.find(n => n.id === nodeId)
      if (node?.data?.status === 'error' && node?.data?.error) {
        setErrorModal({
          open: true,
          nodeLabel: node.data.label || 'Node',
          error: node.data.error,
        })
      }
    }

    // Auto-expand right panel when selecting a node
    if (nodeId && rightPanelCollapsed) {
      setRightPanelCollapsed(false)
    }
  }, [rightPanelCollapsed, nodes])

  // Handle adding new node from palette drop
  const handleNodeAdd = useCallback((newNode) => {
    setNodes((nds) => [...nds, newNode])
    setSelectedNodeId(newNode.id)
  }, [setNodes])

  // Handle config change from panel
  const handleConfigChange = useCallback((nodeId, key, value) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== nodeId) return node

        if (key === '_label') {
          return { ...node, data: { ...node.data, label: value } }
        }
        if (key === '_enabled') {
          return { ...node, data: { ...node.data, enabled: value } }
        }

        return {
          ...node,
          data: {
            ...node.data,
            config: { ...node.data.config, [key]: value }
          }
        }
      })
    )
  }, [setNodes])

  // Handle node deletion
  const handleDeleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId))
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null)
    }
  }, [setNodes, setEdges, selectedNodeId])

  // Handle auto-layout
  const handleLayout = useCallback((layoutedNodes, layoutedEdges) => {
    setNodes(layoutedNodes)
    setEdges(layoutedEdges)
  }, [setNodes, setEdges])

  // Extract analysis context from nodes that have code analysis results
  const analysisContext = useMemo(() => {
    // Find a node with code analysis execution result
    const analysisNode = nodes.find(n =>
      n.data?.executionResult?.jobs?.codeanalysis_job_id ||
      n.data?.nodeType === 'pipeline-code-analysis' ||
      n.data?.nodeType === 'code-analysis'
    )

    if (analysisNode?.data?.executionResult) {
      const result = analysisNode.data.executionResult
      const jobId = result.jobs?.codeanalysis_job_id
      const mainProgram = result.main_program
      // Extract base name from main program (e.g., 'IFPR321.CBL' -> 'ifpr321')
      const baseName = mainProgram?.toLowerCase().replace(/\.cbl$/i, '').replace(/\.cob$/i, '')

      if (jobId && baseName) {
        return { jobId, baseName, mainProgram }
      }
    }
    return null
  }, [nodes])

  // Handle opening a phase view
  const handleOpenView = useCallback((viewName, nodeData = null) => {
    console.log('[DesignerView] Opening view:', viewName, 'PHASE_VIEWS has it:', !!PHASE_VIEWS[viewName])
    console.log('[DesignerView] nodeData passed:', nodeData)

    // If nodeData is passed, use it directly. Otherwise, try to get from selected node.
    let context = null
    const currentSelectedNode = nodes.find(n => n.id === selectedNodeId)
    const nodeId = currentSelectedNode?.id
    // Used for routing intercept (service-gen nodes get their own dedicated view)
    const selectedNodeType = (nodeData?.nodeType || currentSelectedNode?.data?.nodeType || '').toLowerCase()

    // FIX 2026-02-12: Use executionProgress as PRIMARY source of truth (works while paused!)
    // Priority:
    // 1. nodeData.executionResult (if passed directly)
    // 2. executionProgress[nodeId]?.result (current execution - works while paused/executing)
    // 3. node.data.executionResult (fallback for completed nodes from previous runs)
    const progress = executionProgress?.[nodeId]
    const executionResult = nodeData?.executionResult || progress?.result || currentSelectedNode?.data?.executionResult

    console.log(' [OPEN_VIEW] Node:', nodeId, 'View:', viewName)
    console.log(' [OPEN_VIEW] Progress status:', progress?.status)
    console.log(' [OPEN_VIEW] Has result:', !!executionResult)
    console.log(' [OPEN_VIEW] Result source:', nodeData?.executionResult ? 'nodeData' : progress?.result ? 'executionProgress' : currentSelectedNode?.data?.executionResult ? 'node.data' : 'NONE')
    console.log('[DesignerView] executionResult:', executionResult)

    if (executionResult) {
      // 2026-01-28: Accumulate ALL job IDs from ALL completed nodes
      // This ensures Code Editor gets ingest_job_id even if Code Analysis doesn't preserve it
      const accumulatedJobs = {}
      nodes.forEach(node => {
        const nodeResult = node.data?.executionResult
        if (nodeResult?.jobs) {
          Object.assign(accumulatedJobs, nodeResult.jobs)
        }
      })
      // Also merge current node's jobs (in case it's passed via nodeData)
      if (executionResult.jobs) {
        Object.assign(accumulatedJobs, executionResult.jobs)
      }

      console.log('[DesignerView] Accumulated jobs from all nodes:', accumulatedJobs)

      const jobs = accumulatedJobs

      // 2026-01-29: FIX - Use job_id from clicked node's type, not global priority
      // Bug: "Open Analysis" was using refactor job_id because priority put refactor first
      // Fix: Match node type to its specific job_id
      const nodeType = (nodeData?.nodeType || currentSelectedNode?.data?.nodeType || '').toLowerCase()

      let jobId = null
      if (nodeType.includes('code-analysis') || nodeType.includes('codeanalysis')) {
        jobId = jobs.codeanalysis_job_id
      } else if (nodeType.includes('refactor') || nodeType.includes('code-refactor')) {
        jobId = jobs.coderefactor_job_id
      } else if (nodeType.includes('dependency') || nodeType.includes('dependencymapper')) {
        jobId = jobs.dependencymapper_job_id
      } else if (nodeType.includes('data-analysis') || nodeType.includes('dataanalysis')) {
        jobId = jobs.dataanalysis_job_id
      } else if (nodeType.includes('discovery')) {
        jobId = jobs.discovery_job_id
      } else if (nodeType.includes('architecture')) {
        jobId = jobs.architecture_job_id
      } else if (nodeType.includes('service-gen') || nodeType.includes('service-generation')) {
        jobId = jobs.service_generation_job_id
      } else if (nodeType.includes('java-validation') || nodeType.includes('javavalidation')) {
        jobId = jobs.java_validation_job_id
      } else if (nodeType.includes('pre-flight') || nodeType.includes('preflight')) {
        jobId = jobs.pre_flight_job_id
      } else if (nodeType.includes('java-pack') || nodeType.includes('javapackag')) {
        jobId = jobs.java_packaging_job_id
      }

      // Fallback to priority if node type doesn't match
      if (!jobId) {
        jobId = jobs.pre_flight_job_id ||
                jobs.coderefactor_job_id ||
                jobs.codeanalysis_job_id ||
                jobs.dependencymapper_job_id ||
                jobs.dataanalysis_job_id ||
                jobs.discovery_job_id ||
                jobs.architecture_job_id ||
                jobs.service_generation_job_id
      }

      // Build context with all available job IDs
      context = {
        // Pass all job IDs so views can pick what they need
        jobs,
        jobId, // Primary job ID for views that expect it
        // Also expose specific job IDs for convenience
        dependencyMapperJobId: jobs.dependencymapper_job_id,
        // Code analysis specific - use base_name if available, otherwise derive from main_program
        baseName: executionResult.base_name || executionResult.main_program?.toLowerCase().replace(/\.cbl$/i, '').replace(/\.cob$/i, ''),
        mainProgram: executionResult.main_program,
        // Refactor specific
        className: executionResult.class_name || 'Unknown',
        artifactsPath: executionResult.artifacts_path,
        // Gate report specific — passed from GateStatusPanel via nodeData._gateType
        gateType: nodeData?._gateType || null,
        // Re-run context — needed by ResolveIssuesView to call /java-validation/start or /pre-flight/start
        scout_account_id: project?.account || null,
        application_name: project?.name || null,
        user_id: (() => {
          try { return JSON.parse(localStorage.getItem('modernizeit_user'))?.user_id || null } catch (_) { return null }
        })(),
      }
    }

    // For gate-report / resolve-issues: nodeData may carry _gateType
    // even when no executionResult exists (node hasn't run yet).
    if (!context && nodeData?._gateType) {
      const userId = (() => {
        try { return JSON.parse(localStorage.getItem('modernizeit_user'))?.user_id || null } catch (_) { return null }
      })()
      context = {
        gateType: nodeData._gateType,
        jobId: null,
        jobs: {},
        scout_account_id: project?.account || null,
        application_name: project?.name || null,
        user_id: userId,
      }
    }
    // Ensure gateType is always in context for gate/resolve views
    if (context && nodeData?._gateType && !context.gateType) {
      context = { ...context, gateType: nodeData._gateType }
    }

    console.log(' [OPEN_VIEW_CONTEXT] Final context:', {
      viewName,
      nodeId,
      jobId: context?.jobId,
      baseName: context?.baseName,
      mainProgram: context?.mainProgram,
      artifactsPath: context?.artifactsPath,
      hasJobs: !!context?.jobs,
      jobKeys: context?.jobs ? Object.keys(context.jobs) : []
    })
    console.log('[DesignerView] Setting viewContext:', context)
    setViewContext(context)

    // Intercept 'code-editor' for service-gen nodes → dedicated ServiceGenerationView
    const effectiveViewName =
      viewName === 'code-editor' &&
      (selectedNodeType.includes('service-gen') || selectedNodeType.includes('service-generation'))
        ? 'service-gen-editor'
        : viewName

    setActivePhaseView(prev => {
      previousPhaseViewRef.current = prev
      return effectiveViewName
    })
  }, [nodes, selectedNodeId, executionProgress])

  // Handle closing phase view (back to workflow)
  const handleCloseView = useCallback(() => {
    setActivePhaseView(null)
    setViewContext(null)
  }, [])

  // Called by GateReportView when AI Resolve rerun produces a new gate job ID.
  // Updates viewContext.jobs so remounting GateReportView (e.g. after Back from
  // Fix & Continue) initialises from pflt_NEW, not the stale mount-time pflt_OLD.
  const handleGateJobUpdated = useCallback((newJobId) => {
    setViewContext(prev => {
      if (!prev || !newJobId) return prev
      const gateType = prev.gateType
      const jobKey = gateType === 'structural'
        ? 'java_validation_job_id'
        : gateType === 'package'
        ? 'java_packaging_job_id'
        : 'pre_flight_job_id'
      if (import.meta.env.DEV)
        console.log(' [DesignerView] handleGateJobUpdated', {
          gateType,
          jobKey,
          prevJobId:  prev.jobs?.[jobKey],
          newJobId,
          changed:    prev.jobs?.[jobKey] !== newJobId,
        })
      return {
        ...prev,
        jobId: newJobId,
        jobs: { ...(prev.jobs || {}), [jobKey]: newJobId },
      }
    })
  }, [])

  // Navigate from gate-report → resolve-issues for a specific finding.
  // Accepts optional updatedJobId (set when called after AI Resolve rerun) so that
  // viewContext.jobs is updated to pflt_NEW before the navigation takes place.
  // Without this, Fix & Continue would open with pflt_OLD and show 20 blockers.
  const handleResolveIssue = useCallback((finding, updatedJobId) => {
    if (import.meta.env.DEV)
      console.log(' [DesignerView] handleResolveIssue', { findingId: finding.id, updatedJobId })
    setViewContext(prev => {
      const base = { ...(prev || {}), activeFinding: finding }
      if (!updatedJobId) return base
      const gateType = prev?.gateType
      const jobKey = gateType === 'structural'
        ? 'java_validation_job_id'
        : gateType === 'package'
        ? 'java_packaging_job_id'
        : 'pre_flight_job_id'
      return {
        ...base,
        jobId: updatedJobId,
        jobs: { ...(prev?.jobs || {}), [jobKey]: updatedJobId },
      }
    })
    setActivePhaseView(prev => {
      previousPhaseViewRef.current = prev
      return 'resolve-issues'
    })
  }, [])

  // Run workflow
  const handleRun = async () => {
    console.log('=== handleRun CALLED ===')
    console.log('project:', project)
    console.log('nodes:', nodes)
    console.log('nodes.length:', nodes.length)

    // Validation with logging
    if (!project) {
      addLog('No project selected. Please select a project from the landing page.', 'error')
      console.error('No project selected')
      return
    }

    if (nodes.length === 0) {
      addLog('No nodes on canvas to execute. Add nodes to the workflow first.', 'error')
      console.error('No nodes on canvas')
      return
    }

    console.log('Running workflow:', {
      account: project.account,
      application: project.name,
      nodes,
      edges
    })

    if (executeWorkflow) {
      try {
        // Build account and application objects from project
        const account = { account_id: project.account }
        const application = { app_name: project.name }

        // Pass the current canvas nodes/edges as workflowData
        const workflowData = {
          id: currentWorkflow?.id || 'temp-workflow',
          nodes,
          edges,
        }
        const result = await executeWorkflow(account, application, DEFAULT_API_BASE_URL, workflowData)
        console.log('Workflow execution result:', result)
      } catch (err) {
        addLog(`Workflow execution failed: ${err.message}`, 'error')
        console.error('Failed to execute workflow:', err)
      }
    }
  }

  // Assistance wizard one-click run.
  // Wiring path: wizard zip → stagedFiles entry (same shape as FileUploadDropzone) →
  //   injected into upload/ingest node config → executeWorkflow() reads it via ingestExecutor.
  // Uses executeWorkflow() — the same function used by the toolbar Run button.
  // Closes the wizard immediately; user watches execution on the canvas.
  const handleAssistanceRun = useCallback((zipFile) => {
    if (!project) {
      addLog('No project selected. Please select a project first.', 'error')
      return
    }
    if (nodes.length === 0) {
      addLog('No nodes on canvas to execute. Add nodes to the workflow first.', 'error')
      return
    }

    // Build the stagedFiles entry in the exact shape ingestExecutor.js expects:
    //   { id, file (Blob/File), name, size, status, progress }
    // Previously this was just [zipFile] (bare File object) which caused
    // "Staged file is invalid" because ingestExecutor reads stagedFile.file (not the entry itself).
    const stagedFileEntry = zipFile
      ? {
          id: `${zipFile.name}-${Date.now()}`,
          file: zipFile,           // ingestExecutor reads stagedFiles[0].file
          name: zipFile.name,
          size: zipFile.size,
          status: 'ready',
          progress: 0,
        }
      : null

    console.log('[ASSIST_WIZ] staging start — file:', zipFile?.name, 'size:', zipFile?.size)

    // Build a local copy of nodes with the staged entry injected into the upload/ingest node.
    // Passed directly to executeWorkflow to avoid a React setNodes update race condition.
    let runNodes = nodes
    if (stagedFileEntry) {
      runNodes = nodes.map(n => {
        const nt = n.data?.nodeType
        if (nt === 'upload-files' || nt === 'pipeline-upload' || nt === 'pipeline-ingest') {
          console.log(
            '[ASSIST_WIZ] injecting stagedFiles into node:',
            n.id,
            '| keys: id, file, name, size, status, progress'
          )
          return {
            ...n,
            data: {
              ...n.data,
              config: { ...n.data.config, stagedFiles: [stagedFileEntry] },
            },
          }
        }
        return n
      })
    }

    console.log('[ASSIST_WIZ] staging complete — triggering executeWorkflow()')

    // Close the wizard and log — user can watch canvas execution from here
    setShowAssistanceWizard(false)
    addLog('Workflow started — watch progress and open reports as nodes finish.', 'start')

    // Fire the pipeline — uses executeWorkflow(), same as the toolbar Run button
    const account = { account_id: project.account }
    const application = { app_name: project.name }
    const workflowData = {
      id: currentWorkflow?.id || 'temp-workflow',
      nodes: runNodes,
      edges,
    }
    executeWorkflow(account, application, DEFAULT_API_BASE_URL, workflowData).catch(err => {
      addLog(`Workflow execution failed: ${err.message}`, 'error')
    })
  }, [project, nodes, edges, currentWorkflow, executeWorkflow, addLog])

  // Inject the Full Pipeline template onto the canvas programmatically.
  // Uses generateTemplateNodes() from nodeRegistry — the same function the drag-drop
  // handler in WorkflowCanvas uses when the user drops the template from the sidebar.
  const handleInsertFullPipeline = useCallback(() => {
    const { nodes: templateNodes, edges: templateEdges } = generateTemplateNodes(
      'template-full-pipeline',
      100,
      100
    )
    setNodes(templateNodes)
    setEdges(templateEdges)
  }, [setNodes, setEdges])

  // Abort running workflow
  const handleAbort = () => {
    if (abortExecution) {
      abortExecution()
    }
  }

  // Pause running workflow
  const handlePause = () => {
    if (pauseExecution) {
      pauseExecution()
    }
  }

  // Resume paused workflow
  const handleResume = () => {
    if (resumeExecution) {
      resumeExecution()
    }
  }

  // Save workflow
  const handleSave = async () => {
    console.log('Saving workflow:', { nodes, edges })
    if (saveWorkflow) {
      try {
        await saveWorkflow({
          name: currentWorkflow?.name || 'Untitled Workflow',
          nodes,
          edges,
        })
      } catch (err) {
        console.error('Failed to save workflow:', err)
      }
    }
  }

  // Clear canvas
  const handleClear = () => {
    setNodes([])
    setEdges([])
    setSelectedNodeId(null)
  }

  // Export workflow to JSON file
  const handleExportWorkflow = () => {
    const workflowData = {
      name: editedName || currentWorkflow?.name || 'Untitled Workflow',
      version: '1.0',
      exportedAt: new Date().toISOString(),
      nodes: nodes.map(n => ({
        ...n,
        data: {
          ...n.data,
          status: null,
          error: null,
          executionResult: null, // Don't export execution results
        }
      })),
      edges,
    }
    const json = JSON.stringify(workflowData, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${workflowData.name.replace(/\s+/g, '_')}_workflow.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Import workflow from JSON file
  const fileInputRef = useRef(null)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleImportWorkflow = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const workflowData = JSON.parse(e.target.result)

        if (!workflowData.nodes || !Array.isArray(workflowData.nodes)) {
          alert('Invalid workflow file: missing nodes array')
          return
        }

        // Load the workflow
        setNodes(workflowData.nodes)
        setEdges(workflowData.edges || [])
        setEditedName(workflowData.name || 'Imported Workflow')
        setSelectedNodeId(null)

        console.log('Workflow imported:', workflowData.name)
      } catch (err) {
        alert(`Failed to import workflow: ${err.message}`)
        console.error('Import error:', err)
      }
    }
    reader.readAsText(file)

    // Reset input so same file can be selected again
    event.target.value = ''
  }

  // Start editing workflow name
  const handleStartEditName = () => {
    setEditedName(currentWorkflow?.name || 'Untitled Workflow')
    setIsEditingName(true)
  }

  // Save workflow name
  const handleSaveName = () => {
    const newName = editedName.trim() || 'Untitled Workflow'
    // Update via the hook if available, otherwise just close
    if (updateWorkflowNodes) {
      // For now, we update the name through saveWorkflow
      // The name will be saved when user clicks Save
    }
    setIsEditingName(false)
  }

  // Cancel editing workflow name
  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  // Handle name input key press
  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveName()
    } else if (e.key === 'Escape') {
      handleCancelEditName()
    }
  }

  // Re-run workflow (same settings)
  const handleRerun = async () => {
    // Reset all node statuses first
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          status: null,
          error: null,
          // Keep executionResult from previous run for reference
        },
      }))
    )
    // Clear logs and run again
    clearLogs()
    await handleRun()
  }

  // Re-run a single node — called by NodeConfigPanel when "Re-run Node" completes
  const handleNodeRerun = useCallback((nodeId, nodeStatus, result) => {
    setNodes(nds => nds.map(n => {
      if (n.id !== nodeId) return n
      return {
        ...n,
        data: {
          ...n.data,
          status: nodeStatus,
          ...(result ? { executionResult: result } : {}),
        },
      }
    }))
  }, [setNodes])

  // Handle Save Flow button click
  const handleSaveFlowClick = () => {
    if (!project) {
      alert('No project selected. Please select a project first.')
      return
    }
    if (nodes.length === 0) {
      alert('No nodes to save. Please build a workflow first.')
      return
    }
    setSaveFlowDialogOpen(true)
  }

  // Handle flow saved successfully
  const handleFlowSaved = (result) => {
    console.log('Flow saved successfully:', result)
    alert(`Flow "${result.name}" saved successfully!`)
    // Trigger refresh of SavedFlowsPanel
    setFlowsRefreshTrigger(prev => prev + 1)
  }

  // Handle load flow from SavedFlowsPanel
  const handleLoadFlow = (flowDetail) => {
    console.log('Loading flow:', flowDetail)

    // Extract flow data
    const flowData = flowDetail.flow_data
    if (!flowData || !flowData.nodes) {
      alert('Invalid flow data')
      return
    }

    // Ensure all nodes have proper ReactFlow structure
    const validatedNodes = flowData.nodes.map((node, index) => {
      // Ensure position exists
      const position = (node.position && typeof node.position.x !== 'undefined' && typeof node.position.y !== 'undefined')
        ? node.position
        : { x: 100 + (index * 250), y: 100 }

      // Ensure data object exists
      const data = node.data || {
        nodeType: node.type || 'unknown',
        label: node.type || 'Node',
        config: {},
        enabled: true,
        status: null
      }

      // Ensure correct type (must be 'workflowNode' for ReactFlow)
      return {
        ...node,
        id: node.id || `node-${Date.now()}-${index}`,
        type: 'workflowNode',
        position,
        data
      }
    })

    // Load nodes and edges
    setNodes(validatedNodes)
    setEdges(flowData.edges || [])

    // Update workflow name
    setEditedName(flowDetail.name)

    // Optionally update node statuses based on job_statuses
    if (flowDetail.job_statuses && Object.keys(flowDetail.job_statuses).length > 0) {
      setNodes((nds) =>
        nds.map((node) => {
          const jobStatus = flowDetail.job_statuses[node.id]
          if (jobStatus) {
            return {
              ...node,
              data: {
                ...node.data,
                status: jobStatus === 'completed' ? 'complete' : jobStatus === 'failed' ? 'error' : jobStatus,
              },
            }
          }
          return node
        })
      )
    }

    console.log(`Flow "${flowDetail.name}" loaded with ${flowData.nodes.length} nodes`)
    alert(`Flow "${flowDetail.name}" loaded successfully!`)
  }

  // Build job mappings from current nodes
  const buildJobMappings = () => {
    const mappings = {}
    nodes.forEach((node) => {
      // Check if node has a job_id in executionResult or config
      const jobId = node.data?.executionResult?.job_id || node.data?.config?.job_id
      if (jobId) {
        mappings[node.id] = jobId
      }
    })
    return mappings
  }

  // Register workflow actions with parent (for navbar buttons)
  useEffect(() => {
    if (onRegisterActions) {
      onRegisterActions({
        run: handleRun,
        save: handleSaveFlowClick,
      })
    }
    // Cleanup on unmount
    return () => {
      if (onRegisterActions) {
        onRegisterActions({ run: null, save: null })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterActions])

  // If a phase view is active, render it instead of the workflow canvas
  if (activePhaseView && PHASE_VIEWS[activePhaseView]) {
    if (import.meta.env.DEV && (activePhaseView === 'gate-report' || activePhaseView === 'resolve-issues')) {
      const vc = viewContext || {}
      console.log(` [DesignerView] rendering ${activePhaseView}`, {
        gateType:               vc.gateType,
        jobId:                  vc.jobId,
        pre_flight_job_id:      vc.jobs?.pre_flight_job_id,
        java_validation_job_id: vc.jobs?.java_validation_job_id,
      })
    }
    const PhaseViewComponent = PHASE_VIEWS[activePhaseView]

    // Get selected node info for code editor
    const selectedNode = nodes.find(n => n.id === selectedNodeId)
    const nodeType = selectedNode?.data?.nodeType

    // Determine source type for Code Editor based on node type
    const codeEditorSource =
      (nodeType === 'code-refactor' || nodeType === 'pipeline-refactor') ? 'refactor' :
      (nodeType === 'service-generation' || nodeType === 'pipeline-service-gen') ? 'service-generation' :
      'analysis'

    // Pass context to views - use stored viewContext
    // Most views need analysisContext for fetching data by job_id
    const viewProps = {
      onBack: handleCloseView,
      // Pass analysisContext to all views that need it
      analysisContext: viewContext || analysisContext,
      // Code editor specific props
      ...(activePhaseView === 'code-editor' ? { source: codeEditorSource, nodeType } : {}),
      // Service generation dedicated view
      ...(activePhaseView === 'service-gen-editor' ? { source: 'service-generation', nodeType } : {}),
      // Gate report: pass resolve-issues navigation callback + job-update callback
      ...(activePhaseView === 'gate-report' ? { onResolveIssue: handleResolveIssue, onJobUpdated: handleGateJobUpdated } : {}),
      // Resolve issues: back goes to wherever we came from (gate-report or workflow graph)
      ...(activePhaseView === 'resolve-issues' ? {
        onBack: () => setActivePhaseView(previousPhaseViewRef.current || null),
        onResumePipeline: (newGateJobId, gateType) => {
          const jobKey = gateType === 'structural'
            ? 'java_validation_job_id'
            : gateType === 'package'
            ? 'java_packaging_job_id'
            : 'pre_flight_job_id'
          setNodes(prev => prev.map(n => {
            if (n.id !== selectedNodeId) return n
            return {
              ...n,
              data: {
                ...n.data,
                executionResult: {
                  ...(n.data.executionResult || {}),
                  jobs: { ...(n.data.executionResult?.jobs || {}), [jobKey]: newGateJobId },
                },
              },
            }
          }))
          setActivePhaseView(null)
        },
      } : {}),
    }
    console.log('[DesignerView] viewProps:', viewProps)
    return <PhaseViewComponent {...viewProps} />
  }

  return (
    <div className="designer-view">
      {/* Toolbar */}
      <div className="designer-toolbar">
        <div className="toolbar-left">
          <button className="toolbar-btn home-btn" onClick={onBackToLanding} title="Back to Home">
            <Home size={16} />
          </button>
          <span className="toolbar-divider" />
          {isEditingName ? (
            <div className="workflow-name-edit">
              <input
                ref={nameInputRef}
                type="text"
                className="workflow-name-input"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                onBlur={handleSaveName}
                placeholder="Enter workflow name"
              />
              <button className="name-edit-btn save" onClick={handleSaveName} title="Save name">
                <Check size={14} />
              </button>
              <button className="name-edit-btn cancel" onClick={handleCancelEditName} title="Cancel">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button className="workflow-name-btn" onClick={handleStartEditName} title="Click to rename workflow">
              <span className="workflow-name">{editedName || currentWorkflow?.name || 'Untitled Workflow'}</span>
              <Pencil size={12} className="edit-icon" />
            </button>
          )}
        </div>
        <div className="toolbar-center">
          {executing ? (
            <>
              {paused ? (
                <Tooltip simple={UI_TOOLTIPS.resumeWorkflow.simple} advanced={UI_TOOLTIPS.resumeWorkflow.advanced} placement="bottom">
                  <button className="toolbar-btn resume-btn" onClick={handleResume}>
                    <Play size={16} />
                    Resume
                  </button>
                </Tooltip>
              ) : (
                <Tooltip simple={UI_TOOLTIPS.pauseWorkflow.simple} advanced={UI_TOOLTIPS.pauseWorkflow.advanced} placement="bottom">
                  <button className="toolbar-btn pause-btn" onClick={handlePause}>
                    <Pause size={16} />
                    Pause
                  </button>
                </Tooltip>
              )}
              <Tooltip simple={UI_TOOLTIPS.stopWorkflow.simple} advanced={UI_TOOLTIPS.stopWorkflow.advanced} placement="bottom">
                <button className="toolbar-btn abort-btn" onClick={handleAbort}>
                  <Square size={16} />
                  Stop
                </button>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip simple={UI_TOOLTIPS.runWorkflow.simple} advanced={UI_TOOLTIPS.runWorkflow.advanced} placement="bottom">
                <button
                  className="toolbar-btn run-btn"
                  onClick={handleRun}
                  disabled={!project}
                >
                  <Play size={16} />
                  Run
                </button>
              </Tooltip>
              <button
                className="toolbar-btn assistance-btn"
                onClick={() => setShowAssistanceWizard(true)}
                disabled={!project}
                title="Open guided assistance wizard"
              >
                <Wand2 size={15} />
                Assistance
              </button>
            </>
          )}
          {paused && (
            <span className="toolbar-status paused">
              Paused{pausedAtNode ? ` at ${nodes.find(n => n.id === pausedAtNode)?.data?.label || 'node'}` : ''}
            </span>
          )}
        </div>
        <div className="toolbar-right">
          <Tooltip simple={UI_TOOLTIPS.clearCanvas.simple} advanced={UI_TOOLTIPS.clearCanvas.advanced} placement="bottom">
            <button className="toolbar-btn" onClick={handleClear}>
              <RotateCcw size={16} />
            </button>
          </Tooltip>
          <Tooltip simple={UI_TOOLTIPS.runWorkflow.simple} advanced={UI_TOOLTIPS.runWorkflow.advanced} placement="bottom">
            <button
              className="toolbar-btn rerun-btn"
              onClick={handleRerun}
              disabled={executing || nodes.length === 0 || !nodes.some(n => n.data?.status === 'complete' || n.data?.status === 'error')}
            >
              <RefreshCw size={16} />
              Re-run
            </button>
          </Tooltip>
          <span className="toolbar-divider" />
          <Tooltip simple={UI_TOOLTIPS.loadFlow.simple} advanced={UI_TOOLTIPS.loadFlow.advanced} placement="bottom">
            <button className="toolbar-btn" onClick={handleImportClick}>
              <Upload size={16} />
              Load
            </button>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportWorkflow}
          />
          <Tooltip simple={UI_TOOLTIPS.exportFlow.simple} advanced={UI_TOOLTIPS.exportFlow.advanced} placement="bottom">
            <button className="toolbar-btn" onClick={handleExportWorkflow} disabled={nodes.length === 0}>
              <Download size={16} />
              Export
            </button>
          </Tooltip>
          <span className="toolbar-divider" />
          <Tooltip simple={UI_TOOLTIPS.saveFlow.simple} advanced={UI_TOOLTIPS.saveFlow.advanced} placement="bottom">
            <button
              className="toolbar-btn save-flow-btn"
              onClick={handleSaveFlowClick}
              disabled={nodes.length === 0 || executing}
            >
              <Save size={16} />
              Save Flow
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Main content */}
      <div className="designer-content">
        {/* Left Panel - Saved Flows + Node Palette */}
        <div className={`panel-container left-panel ${leftPanelCollapsed ? 'collapsed' : ''}`}>
          {!leftPanelCollapsed && (
            <div className="left-panel-content">
              <SavedFlowsPanel
                onLoadFlow={handleLoadFlow}
                currentAccountId={project?.account}
                refreshTrigger={flowsRefreshTrigger}
              />
              <NodePalette />
            </div>
          )}
          <button
            className="panel-toggle left-toggle"
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            title={leftPanelCollapsed ? 'Show Panels' : 'Hide Panels'}
          >
            {leftPanelCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {/* Center - Canvas */}
        <WorkflowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeSelect={handleNodeSelect}
          onNodeAdd={handleNodeAdd}
          onLayout={handleLayout}
          selectedNodeId={selectedNodeId}
          isExecuting={executing}
        />

        {/* Right Panel - Node Config */}
        <div className={`panel-container right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
          <button
            className="panel-toggle right-toggle"
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            title={rightPanelCollapsed ? 'Show Config Panel' : 'Hide Config Panel'}
          >
            {rightPanelCollapsed ? <PanelRightOpen size={16} /> : <PanelRightClose size={16} />}
          </button>
          {!rightPanelCollapsed && (
            <NodeConfigPanel
              selectedNode={selectedNode}
              onConfigChange={handleConfigChange}
              onDeleteNode={handleDeleteNode}
              onOpenView={(!run || run._isNew || (executionProgress && Object.keys(executionProgress).length > 0)) ? handleOpenView : undefined}
              executing={executing}
              paused={paused}
              executionProgress={executionProgress}
              accountId={project?.account}
              applicationName={project?.name}
              onNodeRerun={handleNodeRerun}
            />
          )}
        </div>
      </div>

      {/* Execution Log Panel */}
      <ExecutionLogPanel
        logs={executionLogs}
        onClear={clearLogs}
      />

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, nodeLabel: '', error: '' })}
        title="Execution Error"
        size="small"
      >
        <div className="error-modal-content">
          <div className="error-modal-icon">
            <AlertCircle size={48} />
          </div>
          <div className="error-modal-node">
            <strong>{errorModal.nodeLabel}</strong> failed to execute
          </div>
          <div className="error-modal-message">
            {errorModal.error}
          </div>
        </div>
        <FormActions>
          <Button
            variant="primary"
            onClick={() => setErrorModal({ open: false, nodeLabel: '', error: '' })}
          >
            Close
          </Button>
        </FormActions>
      </Modal>

      {/* Save Flow Dialog */}
      <SaveFlowDialog
        isOpen={saveFlowDialogOpen}
        onClose={() => setSaveFlowDialogOpen(false)}
        onSaved={handleFlowSaved}
        accountId={project?.account}
        applicationName={project?.name}
        flowData={{ nodes, edges }}
        jobMappings={buildJobMappings()}
      />

      {/* AI Assistant */}
      <AIAssistant
        account={project?.account}
        application={project?.name}
        run={run}
        selectedNode={selectedNode}
        executionResults={executionProgress}
        pageType="workflow"
      />

      {showAssistanceWizard && (
        <AssistanceWizard
          project={project}
          executing={executing}
          nodes={nodes}
          onInsertTemplate={handleInsertFullPipeline}
          onRun={project && executeWorkflow ? handleAssistanceRun : null}
          onClose={() => setShowAssistanceWizard(false)}
        />
      )}

    </div>
  )
}

export default DesignerView
